import { SlidersHorizontal } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { MOBILE_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'

export interface FilterBarProps {
    /** Search and filter controls, left-aligned and wrapping. */
    children: ReactNode
    /**
     * The list's `<SearchInput>`. Optional and purely additive: pass it and the bar
     * keeps search visible at every width while moving `children` into a bottom sheet
     * below `sm`; omit it and the bar renders exactly as it always has, `children`
     * included, at every width.
     */
    search?: ReactNode
    /**
     * Applied-filter count for the mobile trigger's badge, normally `chips.length`.
     * Omit it and the button carries no count.
     */
    activeCount?: number
    /**
     * Column toggle, export, primary action. Right-aligned and never wraps away.
     *
     * Below `sm` these share the row equally. A child that is not button-shaped -- a
     * view toggle, say -- opts out with `data-fill="none"` and keeps its natural width,
     * so it does not claim a button's share of the row and sit in mostly empty space.
     */
    actions?: ReactNode
    /** `<FilterChips>`, on its own row below the controls. */
    chips?: ReactNode
    className?: string
}

export function FilterBar({ children, search, activeCount, actions, chips, className }: FilterBarProps) {
    const [filtersOpen, setFiltersOpen] = useState(false)
    /*
     * The two layouts are different trees rather than one tree styled two ways: on a
     * phone the controls live inside a sheet, which portals to the body. Rendering
     * both and hiding one would duplicate every control and its element ids.
     */
    const isMobile = useMediaQuery(MOBILE_QUERY)
    const collapse = search !== undefined && isMobile

    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 pointer-coarse:gap-2.5">
                    {/* Search keeps the row it needs; the trigger sits beside it rather
                        than below, so the controls cost one row instead of two. */}
                    {search !== undefined && <div className="min-w-0 flex-1 sm:contents">{search}</div>}

                    {collapse ? (
                        <Button
                            type="button"
                            variant="outline"
                            aria-haspopup="dialog"
                            aria-expanded={filtersOpen}
                            onClick={() => setFiltersOpen(true)}
                            className="h-10 shrink-0">
                            <SlidersHorizontal />
                            Filters
                            {activeCount !== undefined && activeCount > 0 && (
                                <Badge variant="neutral" className="tabular ml-0.5">
                                    {activeCount}
                                </Badge>
                            )}
                        </Button>
                    ) : (
                        children
                    )}
                </div>

                {/*
                 * Below `sm` the actions share the width instead of huddling at one end:
                 * each grows from a common basis, so they fill the row and spill onto a
                 * second full-width row once there are too many to fit. At `sm` and up
                 * this is the same non-shrinking right-aligned group it has always been.
                 */}
                {actions && (
                    <div
                        className={cn(
                            'flex flex-wrap items-center justify-end gap-2 pointer-coarse:gap-2.5 sm:shrink-0',
                            // basis-32 is the pivot: two fit a phone row and three do not, so a
                            // four-action bar lands as a tidy 2x2 rather than three plus a lone
                            // full-width straggler. Anything marked `data-fill="none"` keeps its
                            // natural width and the buttons divide what is left.
                            'max-sm:w-full max-sm:justify-start',
                            'max-sm:[&>*:not([data-fill=none])]:grow max-sm:[&>*:not([data-fill=none])]:basis-32'
                        )}>
                        {actions}
                    </div>
                )}
            </div>

            {chips}

            {/*
                Mounted only on a phone, so the controls inside exist once in the document.
                A sheet rather than an inline panel: it rises from the edge the thumb is
                already near, and it can be as tall as the filter set needs without pushing
                the results off the screen.
            */}
            {collapse && (
                <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                    <SheetContent side="bottom" className="max-h-[85dvh] rounded-t-2xl">
                        <SheetHeader>
                            <SheetTitle>Filters</SheetTitle>
                            <SheetDescription>
                                Changes apply as you make them and stay in the URL, so this view can be shared.
                            </SheetDescription>
                        </SheetHeader>
                        <SheetBody>
                            {/* Stacked full-width: a wrapped row of narrow controls is what
                                made these unusable on a phone in the first place. */}
                            <div className="flex flex-col gap-3 [&>*]:w-full">{children}</div>
                        </SheetBody>
                    </SheetContent>
                </Sheet>
            )}
        </div>
    )
}
